import type { Dispatch, SetStateAction } from "react";
import { createContext, useContext, useState } from "react";
import { Outlet } from "react-router-dom";

import { FullScreenErrorAlert } from "@/app/components/legacy-components";
import { usePatientQuery } from "@/app/hooks";
import { useActiveSubscriptions } from "@/features/billing/hooks";
import { LogoLoader } from "@/shared/components";
import type { Subscription } from "@/shared/domains";
import { CATEGORY_SLUG } from "@/shared/domains";
import type { Patient } from "@/shared/models/legacy-types";
import type { MainInfo } from "../models";

interface EmbeddablesForms {
  patient: Patient;
  patientWLSubscription?: Subscription;
  mainInfoFormValues?: MainInfo;

  actions: {
    setMainInfoFormValues: Dispatch<SetStateAction<MainInfo | undefined>>;
  };
}

const EmbeddablesFormsContext = createContext<EmbeddablesForms | null>(null);

export const EmbeddablesFormContextProvider = () => {
  const [mainInfoFormValues, setMainInfoFormValues] = useState<MainInfo>();

  const { data: patient, isLoading, isSuccess, isError } = usePatientQuery();

  const {
    data: patientSubscription,
    isLoading: isLoadingPatientSubscription,
    isSuccess: isSuccessPatientSubscription,
    isError: isErrorPatientSubscription,
  } = useActiveSubscriptions();

  const patientWLSubscription = patientSubscription?.find(
    (subscription) =>
      subscription.currentProduct.category.slug === CATEGORY_SLUG.WEIGHT_LOSS,
  );

  if (isLoading || isLoadingPatientSubscription) {
    return <LogoLoader fullscreen variant="secondary" />;
  }

  return (
    <>
      {isSuccess && isSuccessPatientSubscription && (
        <EmbeddablesFormsContext.Provider
          value={{
            patient,
            patientWLSubscription,
            mainInfoFormValues,
            actions: { setMainInfoFormValues },
          }}
        >
          <Outlet />
        </EmbeddablesFormsContext.Provider>
      )}

      <FullScreenErrorAlert open={isError || isErrorPatientSubscription} />
    </>
  );
};

export const useEmbeddablesForms = () => {
  const context = useContext(EmbeddablesFormsContext);

  if (!context) {
    throw new Error(
      "useEmbeddablesForms must be used within a <EmbeddablesFormsContext.Provider />",
    );
  }

  return context;
};
